import { useNavigate } from 'react-router-dom';
import React, { useRef, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';

import { useAuthContext } from 'src/auth/hooks';

const ACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const BROADCAST_CHANNEL_NAME = 'idle-timer-channel';

export const IdleTimer: React.FC = () => {
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  // eslint-disable-next-line no-undef
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActiveTimeRef = useRef<number>(Date.now());
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const isActiveTabRef = useRef<boolean>(false);

  const resetTimer = useCallback((fromBroadcast: boolean = false) => {
    // eslint-disable-next-line no-console
    console.log('Timer reset');
    const currentTime = Date.now();
    lastActiveTimeRef.current = currentTime;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log('Timeout reached');
      const elapsed = Date.now() - lastActiveTimeRef.current;
      if (elapsed >= ACTIVITY_TIMEOUT_MS) {
        // eslint-disable-next-line no-console
        console.log('Logging out due to inactivity');
        logout();
        navigate(`${paths.auth.firebase.login}?returnTo=${window.location.pathname}`);
      }
    }, ACTIVITY_TIMEOUT_MS);

    // Only broadcast if this reset wasn't triggered by a broadcast
    if (!fromBroadcast) {
      isActiveTabRef.current = true;
      broadcastChannelRef.current?.postMessage({ type: 'reset', time: currentTime });
    }
  }, [logout, navigate]);

  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, handleActivity));
    
    broadcastChannelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    
    broadcastChannelRef.current.onmessage = (event) => {
      if (event.data.type === 'reset') {
        const broadcastTime = event.data.time;
        if (broadcastTime > lastActiveTimeRef.current) {
          isActiveTabRef.current = false;
          resetTimer(true);
        }
      }
    };
    
    resetTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      broadcastChannelRef.current?.close();
    };
  }, [resetTimer, handleActivity]);

  return null;
};